import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.js";

const PAGE_SIZE = 50;

const fmtDate = (iso) => {
  if (!iso) return null;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
};

function PubDateCell({ publication_date, created_at }) {
  if (publication_date) {
    return (
      <span className="font-mono text-xs text-ink-600">
        {fmtDate(publication_date)}
      </span>
    );
  }
  if (created_at) {
    return (
      <span className="font-mono text-xs text-ink-600">
        {fmtDate(created_at)}{" "}
        <em className="text-[10px] not-italic text-ink-400">(entdeckt)</em>
      </span>
    );
  }
  return <span className="text-ink-400">–</span>;
}

export default function Tenders() {
  const [filters, setFilters] = useState({
    q: "",
    country: [],
    region: [],
    source: [],
    keyword: [],
    min_score: 5,
  });
  const [sortColumn, setSortColumn] = useState("score");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [options, setOptions] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Filter-Options und Keywords laden
  useEffect(() => {
    api.getFilters().then(setOptions).catch(() => {});
    api.getKeywords().then(setKeywords).catch(() => {});
  }, []);

  // Drei-Klick-Zyklus: DESC → ASC → Reset (score DESC)
  const handleSort = (column) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortOrder("desc");
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
    } else {
      setSortColumn("score");
      setSortOrder("desc");
    }
    setPage(0);
  };

  // Tender-Liste laden
  useEffect(() => {
    const params = {
      q: filters.q || undefined,
      country: filters.country.length ? filters.country : undefined,
      region: filters.region.length ? filters.region : undefined,
      source: filters.source.length ? filters.source : undefined,
      keyword: filters.keyword.length ? filters.keyword : undefined,
      min_score: filters.min_score,
      sort: sortColumn,
      order: sortOrder,
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    };
    setLoading(true);
    setErr("");
    api
      .listTenders(params)
      .then(setData)
      .catch((e) => setErr(e.message || "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, [filters, page, sortColumn, sortOrder]);

  const patch = (upd) => {
    setPage(0);
    setFilters((f) => ({ ...f, ...upd }));
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
      <FilterSidebar
        options={options}
        keywords={keywords}
        filters={filters}
        onChange={patch}
      />

      <section>
        <ResultsHeader total={data.total} loading={loading} />

        {err && (
          <div className="mb-4 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800">
            {err}
          </div>
        )}

        <TendersTable
          items={data.items}
          loading={loading}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
          onSort={handleSort}
        />

        <Pagination
          page={page}
          totalPages={totalPages}
          onPage={setPage}
        />
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FilterSidebar({ options, keywords, filters, onChange }) {
  return (
    <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start lg:overflow-y-auto lg:max-h-[calc(100dvh-3rem)] lg:pb-6">
      <div>
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-600">
          Suche
        </label>
        <input
          type="search"
          value={filters.q}
          onChange={(e) => onChange({ q: e.target.value })}
          className="input"
          placeholder="Titel, Beschreibung …"
        />
      </div>

      <ScoreFilter
        value={filters.min_score}
        onChange={(v) => onChange({ min_score: v })}
      />

      <MultiFilter
        label="Länder"
        options={options?.countries}
        value={filters.country}
        onChange={(v) => onChange({ country: v })}
      />
      <MultiFilter
        label="Regionen"
        options={options?.regions}
        value={filters.region}
        onChange={(v) => onChange({ region: v })}
      />
      <MultiFilter
        label="Quellen"
        options={options?.sources}
        value={filters.source}
        onChange={(v) => onChange({ source: v })}
      />

      <KeywordFilter
        label="Keywords DE"
        allKeywords={keywords?.de ?? []}
        value={filters.keyword}
        onChange={(v) => onChange({ keyword: v })}
      />
      <KeywordFilter
        label="Keywords EN"
        allKeywords={keywords?.en ?? []}
        value={filters.keyword}
        onChange={(v) => onChange({ keyword: v })}
      />
    </aside>
  );
}

function ScoreFilter({ value, onChange }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium uppercase tracking-wide text-ink-600">
          Min. Score
        </label>
        <span className="font-mono text-xs text-ink-700">{value}</span>
      </div>
      <input
        type="range"
        min="0"
        max="80"
        step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-brand-700"
      />
    </div>
  );
}

function MultiFilter({ label, options, value, onChange }) {
  if (!options || options.length === 0) return null;
  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium uppercase tracking-wide text-ink-600">
          {label}
        </label>
        {value.length > 0 && (
          <button
            onClick={() => onChange([])}
            className="text-xs text-ink-500 hover:text-brand-700"
          >
            zurücksetzen
          </button>
        )}
      </div>
      <div className="space-y-1 rounded-md border border-ink-200 bg-white p-2">
        {options.map((o) => (
          <label
            key={o.value}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-sm hover:bg-ink-50"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-ink-300 text-brand-700 focus:ring-brand-600"
              checked={value.includes(o.value)}
              onChange={() => toggle(o.value)}
            />
            <span className="flex-1 truncate">{o.label}</span>
            <span className="font-mono text-xs text-ink-400">{o.count}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function KeywordFilter({ label, allKeywords, value, onChange }) {
  if (!allKeywords || allKeywords.length === 0) return null;
  const sectionActive = value.filter((kw) => allKeywords.includes(kw));
  const toggle = (kw) =>
    onChange(value.includes(kw) ? value.filter((x) => x !== kw) : [...value, kw]);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-xs font-medium uppercase tracking-wide text-ink-600">
          {label}
        </label>
        {sectionActive.length > 0 && (
          <button
            onClick={() => onChange(value.filter((kw) => !allKeywords.includes(kw)))}
            className="text-xs text-ink-500 hover:text-brand-700"
          >
            zurücksetzen
          </button>
        )}
      </div>
      <div className="space-y-1 rounded-md border border-ink-200 bg-white p-2">
        {allKeywords.map((kw) => (
          <label
            key={kw}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-sm hover:bg-ink-50"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-ink-300 text-brand-700 focus:ring-brand-600"
              checked={value.includes(kw)}
              onChange={() => toggle(kw)}
            />
            <span className="flex-1 truncate">{kw}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ResultsHeader({ total, loading }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-ink-500">
          Ergebnisse
        </div>
        <div className="text-xl font-semibold text-ink-900">
          {loading ? "…" : total.toLocaleString("de-DE")}
          <span className="ml-1 text-sm font-normal text-ink-500">Tender</span>
        </div>
      </div>
    </div>
  );
}

function SortIcon({ column, sortColumn, sortOrder }) {
  if (sortColumn !== column) {
    return <span className="ml-1 text-ink-300" aria-hidden="true">⇅</span>;
  }
  return (
    <span className="ml-1 text-brand-700" aria-hidden="true">
      {sortOrder === "desc" ? "↓" : "↑"}
    </span>
  );
}

function SortTh({ column, label, sortColumn, sortOrder, onSort, className = "" }) {
  const isActive = sortColumn === column;
  const ariaSort = isActive
    ? sortOrder === "asc" ? "ascending" : "descending"
    : "none";
  return (
    <th
      className={`px-4 py-3 font-medium ${className}`}
      aria-sort={ariaSort}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="flex items-center gap-0 cursor-pointer select-none hover:text-ink-900 transition-colors"
      >
        {label}
        <SortIcon column={column} sortColumn={sortColumn} sortOrder={sortOrder} />
      </button>
    </th>
  );
}

function TendersTable({ items, loading, sortColumn, sortOrder, onSort }) {
  if (!loading && items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-ink-200 bg-white p-10 text-center text-sm text-ink-500">
        Keine Treffer mit diesen Filtern.
      </div>
    );
  }
  const thProps = { sortColumn, sortOrder, onSort };
  return (
    <div className="overflow-hidden rounded-lg border border-ink-200 bg-white shadow-soft-lg">
      <table className="w-full text-sm">
        <thead className="bg-ink-50">
          <tr className="text-left text-xs uppercase tracking-wide text-ink-600">
            <SortTh column="score"            label="Score"        {...thProps} />
            <SortTh column="country"          label="Land"         {...thProps} />
            <SortTh column="title"            label="Titel"        {...thProps} />
            <SortTh column="source"           label="Quelle"       {...thProps} className="hidden md:table-cell" />
            <SortTh column="publication_date" label="Veröffentlicht" {...thProps} className="hidden md:table-cell" />
            <SortTh column="deadline"         label="Frist"        {...thProps} />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {items.map((t) => (
            <tr key={t.id} className="hover:bg-ink-50/50">
              <td className="px-4 py-3">
                <ScoreBadge score={t.relevance_score} />
              </td>
              <td className="px-4 py-3 font-mono text-xs text-ink-600">
                {t.country || "—"}
              </td>
              <td className="max-w-xl px-4 py-3">
                <Link
                  to={`/tenders/${t.id}`}
                  className="font-medium text-ink-900 hover:text-brand-700"
                >
                  <span className="line-clamp-2">{t.title}</span>
                </Link>
                {t.keywords_matched?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {t.keywords_matched.slice(0, 3).map((k) => (
                      <span key={k} className="chip">{k}</span>
                    ))}
                    {t.keywords_matched.length > 3 && (
                      <span className="chip">
                        +{t.keywords_matched.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td className="hidden px-4 py-3 text-xs text-ink-600 md:table-cell">
                {t.source_name}
              </td>
              <td className="hidden px-4 py-3 md:table-cell">
                <PubDateCell
                  publication_date={t.publication_date}
                  created_at={t.created_at}
                />
              </td>
              <td className="px-4 py-3 font-mono text-xs text-ink-600">
                {t.deadline ? fmtDate(t.deadline) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && (
        <div className="border-t border-ink-100 bg-ink-50/60 py-2 text-center text-xs text-ink-500">
          Lade …
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score }) {
  const s = score || 0;
  const tone =
    s >= 30 ? "bg-brand-700 text-white"
    : s >= 15 ? "bg-brand-100 text-brand-800 ring-1 ring-inset ring-brand-300"
    : "bg-ink-100 text-ink-700";
  return (
    <span
      className={`inline-flex min-w-[42px] justify-center rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold ${tone}`}
    >
      {s.toFixed(0)}
    </span>
  );
}

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-sm">
      <button
        onClick={() => onPage(Math.max(0, page - 1))}
        disabled={page === 0}
        className="btn-ghost disabled:opacity-40"
      >
        ← Zurück
      </button>
      <span className="font-mono text-xs text-ink-500">
        Seite {page + 1} / {totalPages}
      </span>
      <button
        onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
        className="btn-ghost disabled:opacity-40"
      >
        Weiter →
      </button>
    </div>
  );
}
